import React, { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService from '../../services/api';
import { RiskTierNumber } from '../../tiers/tierCatalog';
import { fmtUsd } from './format';

type Timeframe = '1d' | '1w' | '1m' | '1y';
type MetricMode = 'usd' | 'pct';

interface Props {
  accountId: string;
  riskTier: RiskTierNumber;
  compact?: boolean;
  defaultTimeframe?: Timeframe;
  defaultMetric?: MetricMode;
}

const RETURN_STROKE: Record<RiskTierNumber, string> = {
  1: '#059669',
  2: '#65a30d',
  3: '#0d9488',
  4: '#0284c7',
  5: '#ea580c',
  6: '#7c3aed',
};

const TIMEFRAMES: Array<{ key: Timeframe; label: string }> = [
  { key: '1d', label: '1D' },
  { key: '1w', label: '1W' },
  { key: '1m', label: '1M' },
  { key: '1y', label: '1Y' },
];

const METRICS: Array<{ key: MetricMode; label: string }> = [
  { key: 'usd', label: '$' },
  { key: 'pct', label: '%' },
];

const formatUsdAxis = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const TierPerformanceOverTimeChart: React.FC<Props> = ({
  accountId,
  riskTier,
  compact = false,
  defaultTimeframe = '1w',
  defaultMetric = 'usd',
}) => {
  const [timeframe, setTimeframe] = useState<Timeframe>(defaultTimeframe);
  const [metric, setMetric] = useState<MetricMode>(defaultMetric);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<
    Array<{
      label: string;
      tierPnLUsd: number;
      tierReturnPct: number;
    }>
  >([]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getTierPerformanceHistory(
        accountId,
        riskTier,
        timeframe,
      );
      const err = (res as { error?: string }).error;
      if (err) {
        throw new Error(err);
      }
      if (!Array.isArray(res.points)) {
        throw new Error('Invalid tier performance response');
      }
      setChartData(
        res.points.map((p) => ({
          label: new Date(p.timestamp).toLocaleString(undefined, {
            month: compact ? 'numeric' : 'short',
            day: 'numeric',
            hour: timeframe === '1d' ? '2-digit' : undefined,
            minute: timeframe === '1d' ? '2-digit' : undefined,
          }),
          tierPnLUsd: p.tierPnLUsd,
          tierReturnPct: p.tierReturnPct,
        })),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load tier performance');
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, riskTier, timeframe, compact]);

  useEffect(() => {
    void load();
  }, [load]);

  const height = compact ? 160 : 220;

  return (
    <div className="rounded-lg border border-gray-200/80 bg-white/60 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Tier position P&L over time
          </p>
          <p className="text-[11px] text-gray-400">
            {metric === 'usd'
              ? 'Unrealized P&L ($) for positions in this tier only'
              : 'Return (%) on tier position cost basis'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="flex gap-0.5 rounded-md bg-gray-100 p-0.5"
            role="group"
            aria-label="Chart metric"
          >
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMetric(m.key)}
                aria-pressed={metric === m.key}
                className={`min-w-[2rem] rounded px-2 py-0.5 text-xs font-semibold ${
                  metric === m.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf.key}
                type="button"
                onClick={() => setTimeframe(tf.key)}
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  timeframe === tf.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div
          className="flex items-center justify-center text-xs text-gray-400"
          style={{ height }}
        >
          Loading chart…
        </div>
      ) : error ? (
        <div className="text-xs text-red-600" style={{ minHeight: height }}>
          {error}
        </div>
      ) : chartData.length === 0 ? (
        <div
          className="flex items-center justify-center text-xs text-gray-500"
          style={{ height }}
        >
          No tier position history yet — appears after sync or monitoring samples.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            {metric === 'usd' ? (
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={formatUsdAxis}
                width={48}
              />
            ) : (
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => `${v}%`}
                width={44}
              />
            )}
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
            <Tooltip
              formatter={(value: number) => {
                if (metric === 'pct') {
                  return [
                    `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`,
                    'Return',
                  ];
                }
                return [
                  `${value >= 0 ? '+' : ''}${fmtUsd(value)}`,
                  'Tier P&L',
                ];
              }}
            />
            <Line
              type="monotone"
              dataKey={metric === 'usd' ? 'tierPnLUsd' : 'tierReturnPct'}
              name={metric === 'usd' ? 'Tier P&L ($)' : 'Return (%)'}
              stroke={metric === 'usd' ? '#0ea5e9' : RETURN_STROKE[riskTier]}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TierPerformanceOverTimeChart;
